<script setup lang="ts">
  import { ref } from 'vue';
  import { useMpfFileUpload } from './composables/useMpfFileUpload';
  import { mpfFileUploadDefaults, type MpfFileUploadProps } from './MpfFileUpload.props';
  import './MpfFileUpload.scss';

  const props = withDefaults(defineProps<MpfFileUploadProps>(), mpfFileUploadDefaults);
  const emit = defineEmits<{
    (event: 'update:modelValue', value: File[]): void;
    (event: 'change', value: File[]): void;
    (event: 'invalid', message: string): void;
  }>();

  const fileInput = ref<HTMLInputElement | null>(null);

  const {
    statusText,
    onFileChange,
    openFilePicker,
    chooseButtonText,
    dropInstruction,
    isDisabled,
    isDragOver,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    safeMaxFiles,
    describedBy,
    maxFilesReached,
  } = useMpfFileUpload({
    props,
    emit,
    fileInput,
  });
</script>
<template>
  <div class="mpf-file-upload-wrapper">
    <div
      :id="`${id}-dropzone`"
      class="mpf-file-upload__drop-button"
      :class="{
        'mpf-file-upload__drop-button--empty': !filesCount,
        'mpf-file-upload__drop-button--dragover': isDragOver,
        'mpf-file-upload__drop-button--disabled mpf-file-upload--disabled mpf-file-upload__drop-zone--disabled':
          isDisabled,
      }"
      role="button"
      tabindex="0"
      :aria-labelledby="`${id}-hint-label ${id}-hint-comma ${id}-hint`"
      :aria-describedby="describedBy"
      :aria-disabled="isDisabled"
      @click="openFilePicker"
      @keydown.enter.prevent="openFilePicker"
      @keydown.space.prevent="openFilePicker"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        :id="id"
        ref="fileInput"
        type="file"
        :multiple="safeMaxFiles > 1"
        :accept="accept"
        class="mpf-file-upload mpf-u-margin-bottom-2 mpf-u-visually-hidden"
        :aria-describedby="describedBy"
        :disabled="isDisabled"
        @change="onFileChange"
      />
      <span
        v-if="!multiple"
        :id="`${id}-hint`"
        class="mpf-body mpf-file-upload__status"
        aria-live="polite"
      >
        {{ maxFilesReached ? 'Max files reached' : statusText }}
      </span>

      <span :id="`${id}-hint-comma`" class="mpf-u-visually-hidden">, </span>

      <span class="mpf-file-upload__drop-button-group">
        <span
          :id="`${id}-hint-label`"
          class="mpf-button mpf-file-upload__choose-files-button mpf-button--secondary"
        >
          {{ chooseButtonText }}
        </span>

        <span class="mpf-body mpf-file-upload__drop-instruction">
          {{ dropInstruction }}
        </span>
      </span>
    </div>
  </div>
</template>
